import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter, HashRouter, MemoryRouter, Route, Routes } from 'react-router-dom';
import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { Provider } from 'react-redux';
import { createLogger } from 'redux-logger';

import makeAppReducer from './ducks';
import AuctionsList from './containers/AuctionsList';
import AuctionPage from './containers/AuctionPage';
import AuctioneerPage from './containers/AuctioneerPage';
import AuctioneerList from './containers/AuctioneerList';
import {
    getIndexUrl,
    getAuctionListFullUrl,
    getPastAuctionListFullUrl,
    getAuctionUrl,
    getAuctioneerUrl,
    getAuctioneerListUrl
} from './utils/urls';
import {
    ThemeContext,
    getThemeClassName,
    DEFAULT_THEME
} from './utils/theme';
import { shouldUseLogging } from './utils/logging';
import {
    getFeature,
    FEATURE__SHOWCASE,
    FEATURE__BROWSER_ROUTER,
    FEATURE__AUCTIONEERS_ONLY
} from './utils/features';
import WidgetNav from './components/WidgetNav/WidgetNav';

import './App.css';

const RouteNotFound = () => (
    <p>Not found</p>
);

const App = ({
    associationState,
    auctioneer,
    premiumFilter,
    theme = DEFAULT_THEME,
    lockCategory,
    routerBasePath,
    auctionsPerPage,
}) => {
    // Create Redux store only once using useMemo with empty deps
    // Store should only be created once on initial render
    const store = useMemo(() => {
        const logger = createLogger({
            collapsed: true,
            predicate: shouldUseLogging
        });

        return createStore(
            makeAppReducer(),
            {
                associationState,
                premiumFilter,
                auctionsPerPage,
                lockCategory,
                lockToAuctioneer: auctioneer,
            },
            applyMiddleware(thunk, logger)
        );
    }, []); // eslint-disable-line

    const className = getThemeClassName(theme);
    const showcase = getFeature(FEATURE__SHOWCASE);
    const browserRouter = getFeature(FEATURE__BROWSER_ROUTER);
    const auctioneersOnly = getFeature(FEATURE__AUCTIONEERS_ONLY);

    // showcase shouldn't change the URL and we don't need to maintain
    // page state via the URL, so just use MemoryRouter.
    const Router = showcase ? MemoryRouter
        : browserRouter ? BrowserRouter
        : HashRouter;

    // Only pass basename if it has a value (React Router 6 requirement)
    const routerProps = routerBasePath ? { basename: routerBasePath } : {};

    return (
        <Provider store={store}>
            <ThemeContext.Provider value={theme}>
                <Router {...routerProps}>
                    <div className={`al_App ${className}`}>
                        <div className="al_container">
                            <div id="al-top">
                                { /*
                                    div#al-top is used as a workaround to scroll to top
                                    https://stackoverflow.com/a/24191692
                                */ }
                            </div>
                            <WidgetNav />
                            <Routes>
                                <Route
                                    path={getIndexUrl()}
                                    element={auctioneer
                                        ? <AuctioneerPage />
                                        : auctioneersOnly
                                          ? <AuctioneerList />
                                          : <AuctionsList />
                                    }
                                />
                                {!auctioneersOnly && <Route
                                    path={getAuctionListFullUrl(':page?', ':category?', ':region?', ':date?')}
                                    element={<AuctionsList />}
                                />}
                                {!(auctioneersOnly || showcase) && <Route
                                    path={getPastAuctionListFullUrl(':page?', ':category?', ':region?', ':date?')}
                                    element={<AuctionsList past={true} />}
                                />}
                                {!showcase && <Route
                                    path={getAuctioneerListUrl(':page?', ':expertise?', ':search?', ':association?') /* ':sort?', ':location?' */}
                                    element={<AuctioneerList />}
                                />}
                                {!showcase && <Route
                                    path={`${getAuctionUrl(':auctionId')}/*`}
                                    element={<AuctionPage />}
                                />}
                                {!showcase && <Route
                                    path={getAuctioneerUrl(':auctioneerId')}
                                    element={<AuctioneerPage />}
                                />}
                                <Route path="*" element={<RouteNotFound />} />
                            </Routes>
                        </div>
                    </div>
                </Router>
            </ThemeContext.Provider>
        </Provider>
    );
};

App.propTypes = {
    associationState: PropTypes.string,
    auctioneer: PropTypes.string,
    premiumFilter: PropTypes.string,
    theme: PropTypes.string,
    lockCategory: PropTypes.string,
    routerBasePath: PropTypes.string,
    auctionsPerPage: PropTypes.number.isRequired,
};

export default App;
